import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { useSession } from "../../hooks/useSession.js";
import { useSetupProgress } from "../../hooks/useSetupProgress.js";
import { SetupProgressBar } from "./SetupProgressBar.js";
import { SetupCompleteCard } from "./SetupCompleteCard.js";
import { PortalContractsPage } from "./PortalContractsPage.js";
import { PortalServicePage } from "./PortalServicePage.js";
import { PortalDocumentSigningPage } from "./PortalDocumentSigningPage.js";
import { DialerTrainingStep } from "./DialerTrainingStep.js";
import { DialerCalibrationStep } from "./DialerCalibrationStep.js";
import { activeSetupStepIndex, SETUP_STEPS, type SetupStepId } from "./setupSteps.js";
import { Button } from "../ui/Button.js";
import { Skeleton } from "../ui/Skeleton.js";

function isSetupStepId(value: string | null): value is SetupStepId {
  return SETUP_STEPS.some((step) => step.id === value);
}

/**
 * The agent portal's landing page. The progress bar is the first thing on it,
 * and the step the agent is on renders right underneath — no greeting, no cards
 * to click through, no guided tour.
 */
export function PortalSetupPage() {
  const { agent } = useSession();
  const { progress, loading, error, agentId, dialer, refresh, refreshLocal } = useSetupProgress();
  const [searchParams, setSearchParams] = useSearchParams();
  // True only for the agent who finished the last step just now — that's what
  // earns the automatic hand-off to Ring, rather than every later visit.
  const [justCompleted, setJustCompleted] = useState(false);
  // Staff viewing an agent's portal need to reach any step to see what the
  // agent sees, without paying a fee or sitting through nine videos.
  const impersonating = Boolean(agent?.impersonation);

  // Local progress (steps 3 and 4) can change on another tab, so re-read it
  // whenever this page is mounted or brought back to the front.
  useEffect(() => {
    refreshLocal();
    const onFocus = () => refreshLocal();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshLocal]);

  // Steps advance by finishing the one on screen, never by clicking the bar.
  const selectStep = useCallback(
    (id: SetupStepId) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set("step", id);
        return next;
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [setSearchParams]
  );

  // The step in the URL wins when the agent has actually reached it; otherwise
  // the page opens on whatever they still have to do. Impersonating staff can
  // reach every step, finished or not.
  const activeIndex = activeSetupStepIndex(progress);
  const furthestIndex = Math.min(activeIndex, SETUP_STEPS.length - 1);
  const reachableIndex = impersonating ? SETUP_STEPS.length - 1 : furthestIndex;
  const requested = searchParams.get("step");
  const requestedIndex = isSetupStepId(requested)
    ? SETUP_STEPS.findIndex((step) => step.id === requested)
    : -1;
  const selectedIndex = requestedIndex >= 0 && requestedIndex <= reachableIndex ? requestedIndex : furthestIndex;
  const selected = SETUP_STEPS[selectedIndex];
  const allComplete = activeIndex === SETUP_STEPS.length;
  const isLastStep = selectedIndex === SETUP_STEPS.length - 1;

  function renderStep() {
    switch (selected.id) {
      case "contracting":
        return (
          <PortalContractsPage
            variant="step"
            complete={progress.contracting}
            onProceeded={() => {
              refresh();
              selectStep("dialer");
            }}
          />
        );
      case "dialer":
        return (
          <PortalServicePage
            product="DIALER_INTELELEAD"
            variant="step"
            complete={progress.dialer}
            onStepComplete={() => selectStep("training")}
          />
        );
      case "training":
        return (
          <DialerTrainingStep
            agentId={agentId}
            complete={progress.training}
            callDeliveryNumber={dialer?.softPhone?.callDeliveryNumber}
            allowSkip={impersonating}
            onProgressChange={refreshLocal}
            onStepComplete={() => {
              refreshLocal();
              selectStep("desktopApp");
            }}
          />
        );
      case "desktopApp":
        return (
          <DialerCalibrationStep
            agentId={agentId}
            softPhone={dialer?.softPhone ?? null}
            allowSkip={impersonating}
            onBack={() => selectStep("training")}
            onStepComplete={() => {
              refreshLocal();
              selectStep("achForm");
            }}
          />
        );
      case "achForm":
        return (
          <PortalDocumentSigningPage
            documentType="ACH_FORM"
            variant="step"
            onStepComplete={() => {
              refresh();
              selectStep("ccAuthForm");
            }}
          />
        );
      case "ccAuthForm":
        return (
          <PortalDocumentSigningPage
            documentType="CC_AUTH_FORM"
            variant="step"
            onStepComplete={() => {
              setJustCompleted(true);
              refresh();
            }}
          />
        );
    }
  }

  return (
    <section className="space-y-8" aria-label="Setup">
      {error && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-status-rejected-border bg-status-rejected-bg p-4 text-sm text-status-rejected-text"
        >
          <span>Your setup progress could not be refreshed. The rest of your portal is still available.</span>
          <Button size="sm" variant="secondary" onClick={refresh}>
            Retry
          </Button>
        </div>
      )}

      {loading ? (
        <div className="space-y-8">
          <Skeleton className="h-[76px] w-full" />
          <Skeleton className="h-[420px] w-full rounded-card" />
        </div>
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.04 }}
          >
            <SetupProgressBar
              progress={progress}
              selectedId={selected.id}
              onSelectStep={impersonating ? selectStep : undefined}
            />
          </motion.div>

          {impersonating && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border-2 border-status-nigo-border bg-status-nigo-bg px-5 py-3 text-sm font-semibold text-status-nigo-text">
              <span>Impersonating — every step is unlocked and nothing here is gated.</span>
              {!isLastStep && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => selectStep(SETUP_STEPS[selectedIndex + 1].id)}
                >
                  Skip to step {selectedIndex + 2}
                  <span aria-hidden="true">→</span>
                </Button>
              )}
            </div>
          )}

          {/* Staff keep the step content on a finished setup — they're here to
              see what the agent sees, not to be thrown at the Ring portal. */}
          {allComplete && !impersonating ? (
            <SetupCompleteCard redirect={justCompleted} />
          ) : (
            renderStep()
          )}
        </>
      )}
    </section>
  );
}
